<script lang="ts" setup>
import { Skeleton } from '~ds/components/ui/skeleton';
import KUIUnifiedBadgeDetail from '~kui/components/KUI/Unified/Wallet/Badge/KUIUnifiedBadgeDetail.vue';

import { useWalletBadgeDetail } from '~/composables/Award/Wallet/useWalletBadgeDetail';
import { useIaPlaces } from '~/composables/unified/useIaPlaces';
import { AnalyticsOrigin } from '~/constants/analyticsOrigin';

/**
 * A badge, opened from Learn's Badges category: what it is, who it is from, and the experience that
 * earns it, for guests as well as signed-in students (who also see their progress). Starting it
 * opens the experience, which signs a guest up as it does from anywhere else in Learn; a student in
 * the coach shell opens it in the shell.
 */
interface Props {
  slug: string;
  origin?: AnalyticsOrigin;
}

const props = withDefaults(defineProps<Props>(), {
  origin: AnalyticsOrigin.PublicBrowse,
});

const accessibilityStore = useAccessibilityStore();
const places = useIaPlaces();

const { badge, learningAssetCollection, spotlight, trackSpotlightLinkClick } = useWalletBadgeDetail({ slug: props.slug, origin: props.origin });

useSeoMeta({
  title: () => badge.value?.name ?? '',
});

const start = () => {
  const slugToOpen = learningAssetCollection.value?.slug;
  if (!slugToOpen) {
    return;
  }

  void navigateTo(places.inShell
    ? places.collection(slugToOpen)
    : { path: places.collection(slugToOpen), query: { from: 'learn', category: 'badges' } });
};
</script>

<template>
  <div v-if="!badge" class="w-full shrink-0">
    <div class="mx-auto w-full px-4 pt-4 sm:max-w-4xl lg:max-w-5xl">
      <Skeleton class="h-96 rounded-2xl sm:rounded-3xl" />
    </div>
  </div>
  <KUIUnifiedBadgeDetail
    v-else
    :badge="badge"
    :animations-enabled="accessibilityStore.animationsEnabled"
    :spotlight="spotlight"
    @start="start"
    @spotlight-link-click="trackSpotlightLinkClick"
  />
</template>
